import React from 'react';
import { BrowserRouter as Router, Route, Routes, NavLink } from 'react-router-dom';

// Import page components
import ShowingBookerLanding from './pages/ShowingBookerLanding';
import Pricing from './pages/Pricing';
import OurStory from './pages/OurStory';
import PropertyDetails from './pages/PropertyDetails';
import Subscription from './pages/Subscription';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import ForgotPassword from './components/auth/ForgotPassword';

// Legal and policy pages
import Compare from './pages/Compare';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SMSPolicy from './pages/SMSPolicy';
import EmailPolicy from './pages/EmailPolicy';
import TenantRights from './pages/TenantRights';
import AgentLiability from './pages/AgentLiability';
import OwnerProtection from './pages/OwnerProtection';
import Contact from './pages/Contact';
import How from './pages/How';
import About from './pages/About';

// Import auth components
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';

// Dashboard helper components
const DashboardPage = ({ title, description, children }) => (
  <div style={{ padding: '32px 16px', maxWidth: '1100px', margin: '0 auto' }}>
    <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '4px', color: '#1a1a2e' }}>{title}</h2>
    <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '24px' }}>{description}</p>
    {children}
  </div>
);

const StatCard = ({ label, value }) => (
  <div style={{
    background: 'white',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center'
  }}>
    <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#2d5a27', marginBottom: '4px' }}>{value}</div>
    <div style={{ fontSize: '0.8rem', color: '#666' }}>{label}</div>
  </div>
);

const EmptyState = ({ message, buttonText, buttonLink }) => (
  <div style={{
    background: '#f8f9fa',
    border: '1px dashed #ddd',
    borderRadius: '8px',
    padding: '48px 24px',
    textAlign: 'center'
  }}>
    <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: buttonText ? '16px' : '0' }}>{message}</p>
    {buttonText && (
      <a href={buttonLink || '#'} style={{
        display: 'inline-block',
        background: '#2d5a27',
        color: 'white',
        padding: '10px 24px',
        borderRadius: '6px',
        textDecoration: 'none',
        fontSize: '0.9rem',
        fontWeight: '600'
      }}>
        {buttonText}
      </a>
    )}
  </div>
);

const SettingsPlaceholder = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    <div style={{ background: 'white', border: '1px solid #e5e5e5', borderRadius: '8px', padding: '20px' }}>
      <h3 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#1a1a2e' }}>Notification Preferences</h3>
      <p style={{ fontSize: '0.85rem', color: '#666' }}>Configure how and when you receive notifications. Coming soon.</p>
    </div>
    <div style={{ background: 'white', border: '1px solid #e5e5e5', borderRadius: '8px', padding: '20px' }}>
      <h3 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#1a1a2e' }}>Default Notice Settings</h3>
      <p style={{ fontSize: '0.85rem', color: '#666' }}>Set your default notice timing and delivery channels. Coming soon.</p>
    </div>
  </div>
);

const AccountPage = () => {
  const { user } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div style={{ padding: '32px 16px', maxWidth: '600px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '24px', color: '#1a1a2e' }}>Account</h2>

      <div style={{ background: 'white', border: '1px solid #e5e5e5', borderRadius: '8px', padding: '24px', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '16px', color: '#1a1a2e' }}>Profile Information</h3>
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '4px' }}>Email</div>
          <div style={{ fontSize: '0.9rem', color: '#1a1a2e' }}>{user?.email || 'Not available'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '4px' }}>Account ID</div>
          <div style={{ fontSize: '0.85rem', color: '#888', fontFamily: 'monospace' }}>{user?.firebaseUid?.substring(0, 20)}...</div>
        </div>
      </div>

      <div style={{ background: 'white', border: '1px solid #e5e5e5', borderRadius: '8px', padding: '24px' }}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '12px', color: '#1a1a2e' }}>Sign Out</h3>
        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '16px' }}>Sign out of your ShowingBooker account.</p>
        <button
          onClick={handleSignOut}
          style={{
            background: '#dc3545',
            color: 'white',
            border: 'none',
            padding: '10px 24px',
            borderRadius: '6px',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
};

const AppContent = () => {
  const { user, loading } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#2d5a27' }}>ShowingBooker</div>
          <div style={{ color: '#666', marginTop: '8px', fontSize: '0.85rem' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{
        background: '#ffffff',
        borderBottom: '1px solid #e5e5e5',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1100px',
          margin: '0 auto',
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Logo and Slogan */}
          <NavLink to="/" style={{ textDecoration: 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{
                color: '#2d5a27',
                fontSize: '1.15rem',
                fontWeight: '700',
                letterSpacing: '-0.02em'
              }}>
                ShowingBooker
              </span>
              <span style={{
                color: '#666',
                fontSize: '0.7rem',
                marginTop: '1px'
              }}>
                Compliant Showings Made Simple
              </span>
            </div>
          </NavLink>

          {/* Navigation */}
          <nav style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <NavLink
              to="/how"
              style={({ isActive }) => ({
                textDecoration: 'none',
                color: isActive ? '#2d5a27' : '#555',
                padding: '6px 12px',
                fontSize: '0.85rem',
                fontWeight: '500',
                borderRadius: '4px',
                transition: 'color 0.2s'
              })}
            >
              How It Works
            </NavLink>
            <NavLink
              to="/compare"
              style={({ isActive }) => ({
                textDecoration: 'none',
                color: isActive ? '#2d5a27' : '#555',
                padding: '6px 12px',
                fontSize: '0.85rem',
                fontWeight: '500',
                borderRadius: '4px',
                transition: 'color 0.2s'
              })}
            >
              Why Us
            </NavLink>
            <NavLink
              to="/about"
              style={({ isActive }) => ({
                textDecoration: 'none',
                color: isActive ? '#2d5a27' : '#555',
                padding: '6px 12px',
                fontSize: '0.85rem',
                fontWeight: '500',
                borderRadius: '4px',
                transition: 'color 0.2s'
              })}
            >
              About
            </NavLink>
            <NavLink
              to="/contact"
              style={({ isActive }) => ({
                textDecoration: 'none',
                color: isActive ? '#2d5a27' : '#555',
                padding: '6px 12px',
                fontSize: '0.85rem',
                fontWeight: '500',
                borderRadius: '4px',
                transition: 'color 0.2s'
              })}
            >
              Contact
            </NavLink>
            {user ? (
              <>
                <NavLink
                  to="/dashboard"
                  style={{
                    textDecoration: 'none',
                    color: '#555',
                    padding: '6px 12px',
                    fontSize: '0.85rem',
                    fontWeight: '500'
                  }}
                >
                  Dashboard
                </NavLink>
                <button
                  onClick={handleSignOut}
                  style={{
                    background: 'none',
                    border: '1px solid #ddd',
                    color: '#555',
                    padding: '6px 16px',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                    borderRadius: '4px',
                    marginLeft: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <NavLink
                  to="/login"
                  style={{
                    textDecoration: 'none',
                    color: '#555',
                    padding: '6px 12px',
                    fontSize: '0.85rem',
                    fontWeight: '500'
                  }}
                >
                  Log In
                </NavLink>
                <NavLink
                  to="/register"
                  style={{
                    textDecoration: 'none',
                    background: '#2d5a27',
                    color: 'white',
                    padding: '6px 16px',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    marginLeft: '4px'
                  }}
                >
                  Request Access
                </NavLink>
              </>
            )}
          </nav>
        </div>

        {/* Secondary nav for logged-in users */}
        {user && (
          <div style={{ background: '#f8f9fa', borderTop: '1px solid #e5e5e5' }}>
            <div style={{
              maxWidth: '1100px',
              margin: '0 auto',
              padding: '8px 16px',
              display: 'flex',
              gap: '8px'
            }}>
              <NavLink
                to="/properties"
                style={({ isActive }) => ({
                  textDecoration: 'none',
                  color: isActive ? '#2d5a27' : '#666',
                  padding: '4px 12px',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  borderRadius: '4px',
                  background: isActive ? '#e8f5e9' : 'transparent'
                })}
              >
                Properties
              </NavLink>
              <NavLink
                to="/showings"
                style={({ isActive }) => ({
                  textDecoration: 'none',
                  color: isActive ? '#2d5a27' : '#666',
                  padding: '4px 12px',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  borderRadius: '4px',
                  background: isActive ? '#e8f5e9' : 'transparent'
                })}
              >
                Showings
              </NavLink>
              <NavLink
                to="/compliance"
                style={({ isActive }) => ({
                  textDecoration: 'none',
                  color: isActive ? '#2d5a27' : '#666',
                  padding: '4px 12px',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  borderRadius: '4px',
                  background: isActive ? '#e8f5e9' : 'transparent'
                })}
              >
                Compliance
              </NavLink>
              <NavLink
                to="/reports"
                style={({ isActive }) => ({
                  textDecoration: 'none',
                  color: isActive ? '#2d5a27' : '#666',
                  padding: '4px 12px',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  borderRadius: '4px',
                  background: isActive ? '#e8f5e9' : 'transparent'
                })}
              >
                Reports
              </NavLink>
              <NavLink
                to="/settings"
                style={({ isActive }) => ({
                  textDecoration: 'none',
                  color: isActive ? '#2d5a27' : '#666',
                  padding: '4px 12px',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  borderRadius: '4px',
                  background: isActive ? '#e8f5e9' : 'transparent'
                })}
              >
                Settings
              </NavLink>
              <NavLink
                to="/account"
                style={({ isActive }) => ({
                  textDecoration: 'none',
                  color: isActive ? '#2d5a27' : '#666',
                  padding: '4px 12px',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  borderRadius: '4px',
                  background: isActive ? '#e8f5e9' : 'transparent'
                })}
              >
                Account
              </NavLink>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main style={{ flex: 1 }}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<ShowingBookerLanding />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/our-story" element={<OurStory />} />
          <Route path="/login" element={<LoginForm />} />
          <Route path="/register" element={<RegisterForm />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Information pages */}
          <Route path="/compare" element={<Compare />} />
          <Route path="/how" element={<How />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />

          {/* User-specific info pages */}
          <Route path="/tenant-rights" element={<TenantRights />} />
          <Route path="/agent-liability" element={<AgentLiability />} />
          <Route path="/owner-protection" element={<OwnerProtection />} />

          {/* Legal pages */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/sms-policy" element={<SMSPolicy />} />
          <Route path="/email-policy" element={<EmailPolicy />} />

          {/* Protected routes */}
          <Route path="/subscription" element={
            <ProtectedRoute>
              <Subscription />
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage title="Dashboard" description="Your compliance overview">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                  <StatCard label="Properties" value="0" />
                  <StatCard label="Pending Showings" value="0" />
                  <StatCard label="Notices Sent" value="0" />
                  <StatCard label="Compliance Rate" value="100%" />
                </div>
                <EmptyState message="Add your first property to get started" buttonText="Add Property" buttonLink="/properties" />
              </DashboardPage>
            </ProtectedRoute>
          } />
          <Route path="/compliance" element={
            <ProtectedRoute>
              <DashboardPage title="Compliance Center" description="Track all notices and delivery confirmations">
                <EmptyState message="No compliance records yet. Schedule a showing to generate compliance documentation." />
              </DashboardPage>
            </ProtectedRoute>
          } />
          <Route path="/properties" element={
            <ProtectedRoute>
              <DashboardPage title="Properties" description="Manage your tenant-occupied properties">
                <EmptyState message="No properties added yet" buttonText="Add Your First Property" />
              </DashboardPage>
            </ProtectedRoute>
          } />
          <Route path="/showings" element={
            <ProtectedRoute>
              <DashboardPage title="Showings" description="Schedule and track property showings">
                <EmptyState message="No showings scheduled. Add a property first, then schedule a showing." />
              </DashboardPage>
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute>
              <DashboardPage title="Compliance Reports" description="Generate PDF reports for your records">
                <EmptyState message="No reports available. Reports are generated automatically when you send notices." />
              </DashboardPage>
            </ProtectedRoute>
          } />
          <Route path="/templates" element={
            <ProtectedRoute>
              <DashboardPage title="Notice Templates" description="Customize your notification messages">
                <EmptyState message="Default templates are ready to use. Custom templates coming soon." />
              </DashboardPage>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <DashboardPage title="Settings" description="Configure your notification preferences">
                <SettingsPlaceholder />
              </DashboardPage>
            </ProtectedRoute>
          } />
          <Route path="/account" element={
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          } />
          <Route path="/property/:address" element={
            <ProtectedRoute>
              <PropertyDetails />
            </ProtectedRoute>
          } />
        </Routes>
      </main>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
};

export default App;
